import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import HeroBanner from '../components/tw1/HeroBanner';
import { RefreshCw, Upload, Download, FileText, Trash2, AlertTriangle, CheckCircle, HelpCircle, XCircle, Info, Clock, Settings, ShieldAlert, Search, Filter, Import, X, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import * as XLSX from 'xlsx';

const C = '#4f46e5';
const CATS = {
  opnieuw:   { label: 'Opnieuw incasseren', color: '#059669', bg: '#05966910', icon: CheckCircle },
  handmatig: { label: 'Handmatig controleren', color: '#d97706', bg: '#d9770610', icon: HelpCircle },
  niet:      { label: 'Niet opnieuw', color: '#dc2626', bg: '#dc262610', icon: XCircle },
};

function fmt(n) { return n?.toLocaleString('nl-NL', { minimumFractionDigits: 2 }); }

const HARD_BLOCK = ['rekening opgeheven', 'rekeningnummer incorrect', 'geblokkeerd', 'mandaat ongeldig', 'correspondent bank is onjuist', 'info in machtiging niet correct'];

const WERK_ST = ['nieuw', 'in behandeling', 'wachten op klant', 'wachten op intern', 'heropend in Exact', 'opnieuw geselecteerd voor incasso', 'afgerond', 'geblokkeerd'];
const DOSSIER_ST = ['open', 'in opvolging', 'wacht op reactie', 'klaar voor heraanbieding', 'opnieuw aangeboden', 'definitief afgesloten'];
const MEDEW = ['Jesse', 'Kick', 'Johannes', 'Milos', 'Onbekend'];
const WERK_COLORS = { 'nieuw': '#6366f1', 'in behandeling': '#3b82f6', 'wachten op klant': '#d97706', 'wachten op intern': '#ca8a04', 'heropend in Exact': '#8b5cf6', 'opnieuw geselecteerd voor incasso': '#0891b2', 'afgerond': '#059669', 'geblokkeerd': '#dc2626' };

export default function StornoPage() {
  const [mainTab, setMainTab] = useState('analyzer');
  const [file, setFile] = useState(null);
  const [fileNames, setFileNames] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const [wachtdagen, setWachtdagen] = useState(5);
  const [hoogDrempel, setHoogDrempel] = useState(5000);
  // Tab 3 state
  const [werklijst, setWerklijst] = useState([]);
  const [wlLoading, setWlLoading] = useState(false);
  const [wlFilter, setWlFilter] = useState({ werkstatus: '', medewerker: '', dossierstatus: '', search: '' });
  const [selectedItem, setSelectedItem] = useState(null);
  const [editData, setEditData] = useState({});

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const arr = Array.from(fileList);
    setFile(arr[0]);
    setFileNames(arr.map(f => f.name));
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      arr.forEach(f => fd.append('files', f));
      const res = await api.post('/api/storno/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      toast.success(`${res.data.summary.total} storno's geanalyseerd`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verwerking mislukt');
    } finally { setLoading(false); }
  };

  const exportAnalyse = async () => {
    if (!result?.rows) return;
    const wb = XLSX.utils.book_new();
    const hdr = ['Datum', 'Klantnaam', 'IBAN', 'Bedrag', 'Reden', 'Omschrijving', 'Classificatie', 'Advies'];

    // Sheet 1: Alles
    const allData = [hdr, ...result.rows.map(r => [r.datum, r.naam, r.iban, r.bedrag, r.reden, r.omschrijving, CATS[r.classificatie]?.label || r.classificatie, r.advies])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(allData), 'Alles');

    // Sheet 2: Opnieuw
    const opn = result.rows.filter(r => r.classificatie === 'opnieuw');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...opn.map(r => [r.datum, r.naam, r.iban, r.bedrag, r.reden, r.omschrijving, CATS[r.classificatie]?.label, r.advies])]), 'Opnieuw');

    // Sheet 3: Handmatig
    const hand = result.rows.filter(r => r.classificatie === 'handmatig');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...hand.map(r => [r.datum, r.naam, r.iban, r.bedrag, r.reden, r.omschrijving, CATS[r.classificatie]?.label, r.advies])]), 'Handmatig');

    // Sheet 4: Niet opnieuw
    const niet = result.rows.filter(r => r.classificatie === 'niet');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...niet.map(r => [r.datum, r.naam, r.iban, r.bedrag, r.reden, r.omschrijving, CATS[r.classificatie]?.label, r.advies])]), 'Niet opnieuw');

    // Sheet 5: Samenvatting
    const samData = [
      ['Classificatie', 'Aantal', 'Totaalbedrag'],
      ['Opnieuw incasseren', result.summary.opnieuw, result.summary.bedrag_opnieuw],
      ['Handmatig controleren', result.summary.handmatig, result.summary.bedrag_handmatig],
      ['Niet opnieuw incasseren', result.summary.niet, result.summary.bedrag_niet],
      ['', '', ''],
      ['Totaal', result.summary.total, result.summary.total_amount],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(samData), 'Samenvatting');

    const b64 = XLSX.write(wb, { bookType: 'xls', type: 'base64' });
    const a = document.createElement('a');
    const fn = `storno_analyse_${new Date().toISOString().slice(0, 10)}.xls`;
    a.href = `data:application/vnd.ms-excel;base64,${b64}`;
    a.download = fn; a.click();
    toast.success(`Export: ${fn}`);
    try { await api.post('/api/storno/export', { filename: fn, metadata: { total: result.summary.total, opnieuw: result.summary.opnieuw, handmatig: result.summary.handmatig, niet: result.summary.niet } }); } catch(e) { console.error('[Storno] Export log failed:', e); }
  };

  // ─── Tab 3 functions ───
  const loadWerklijst = useCallback(async () => {
    setWlLoading(true);
    try { const res = await api.get('/api/storno/werklijst'); setWerklijst(res.data.items || []); } catch(e) { toast.error('Werklijst laden mislukt'); }
    setWlLoading(false);
  }, []);

  useEffect(() => { if (mainTab === 'werklijst') loadWerklijst(); }, [mainTab, loadWerklijst]);

  const importToWerklijst = async () => {
    if (!result?.rows) return;
    try {
      const res = await api.post('/api/storno/werklijst/import', { items: result.rows });
      toast.success(`${res.data.added} toegevoegd, ${res.data.skipped} overgeslagen`);
      loadWerklijst();
    } catch(e) { toast.error('Import mislukt'); }
  };

  const selectWlItem = (item) => { setSelectedItem(item); setEditData({ ...item }); };
  const closePanel = () => { setSelectedItem(null); setEditData({}); };
  const saveItem = async () => {
    if (!selectedItem) return;
    try {
      await api.put(`/api/storno/werklijst/${selectedItem.id}`, editData);
      toast.success('Opgeslagen');
      setWerklijst(prev => prev.map(w => w.id === selectedItem.id ? { ...w, ...editData } : w));
      setSelectedItem(prev => ({ ...prev, ...editData }));
    } catch(e) { toast.error('Opslaan mislukt'); }
  };
  const markAfgerond = async () => {
    const upd = { ...editData, werkstatus: 'afgerond', dossierstatus: 'definitief afgesloten' };
    setEditData(upd);
    try {
      await api.put(`/api/storno/werklijst/${selectedItem.id}`, upd);
      toast.success('Afgerond');
      setWerklijst(prev => prev.map(w => w.id === selectedItem.id ? { ...w, ...upd } : w));
      setSelectedItem(prev => ({ ...prev, ...upd }));
    } catch(e) { toast.error('Mislukt'); }
  };

  const filteredWl = useMemo(() => {
    return werklijst.filter(w => {
      if (wlFilter.werkstatus && w.werkstatus !== wlFilter.werkstatus) return false;
      if (wlFilter.medewerker && w.medewerker !== wlFilter.medewerker) return false;
      if (wlFilter.dossierstatus && w.dossierstatus !== wlFilter.dossierstatus) return false;
      if (wlFilter.search) {
        const s = wlFilter.search.toLowerCase();
        if (!w.naam?.toLowerCase().includes(s) && !w.iban?.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [werklijst, wlFilter]);

  const wlStats = useMemo(() => {
    const cnt = {}; const amt = {};
    WERK_ST.forEach(s => { cnt[s] = 0; amt[s] = 0; });
    werklijst.forEach(w => { cnt[w.werkstatus] = (cnt[w.werkstatus] || 0) + 1; amt[w.werkstatus] = Math.round(((amt[w.werkstatus] || 0) + (w.bedrag || 0)) * 100) / 100; });
    return { cnt, amt };
  }, [werklijst]);

  const debSummary = useMemo(() => {
    const map = {};
    werklijst.forEach(w => {
      const k = w.naam || 'Onbekend';
      if (!map[k]) map[k] = { naam: k, count: 0, totaal: 0, open: 0, afgerond: 0 };
      map[k].count++; map[k].totaal = Math.round((map[k].totaal + (w.bedrag || 0)) * 100) / 100;
      if (w.werkstatus === 'afgerond') map[k].afgerond++; else map[k].open++;
    });
    return Object.values(map).sort((a, b) => b.totaal - a.totaal);
  }, [werklijst]);

  const exportWerklijst = async () => {
    const wb = XLSX.utils.book_new();
    const hdr = ['Datum', 'Klantnaam', 'IBAN', 'Bedrag', 'Reden', 'Werkstatus', 'Dossierstatus', 'Medewerker', 'Notitie', 'Exact verwerkt'];
    const mp = r => [r.datum, r.naam, r.iban, r.bedrag, r.reden, r.werkstatus, r.dossierstatus, r.medewerker, r.notitie, r.exact_verwerkt ? 'Ja' : 'Nee'];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...werklijst.map(mp)]), 'Alles');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...werklijst.filter(r => !['afgerond', 'geblokkeerd'].includes(r.werkstatus)).map(mp)]), 'Open acties');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...werklijst.filter(r => r.werkstatus === 'wachten op klant').map(mp)]), 'Wachten op klant');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...werklijst.filter(r => r.werkstatus === 'heropend in Exact').map(mp)]), 'Heropend');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...werklijst.filter(r => r.werkstatus === 'afgerond').map(mp)]), 'Afgerond');
    const dhdr = ['Klantnaam', 'Aantal', 'Totaalbedrag', 'Open', 'Afgerond'];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([dhdr, ...debSummary.map(d => [d.naam, d.count, d.totaal, d.open, d.afgerond])]), 'Per debiteur');
    const samData = [['Status', 'Aantal', 'Totaalbedrag'], ...WERK_ST.map(s => [s, wlStats.cnt[s], wlStats.amt[s]]), ['', '', ''], ['Totaal', werklijst.length, werklijst.reduce((s, r) => s + (r.bedrag || 0), 0)]];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(samData), 'Samenvatting');
    const b64 = XLSX.write(wb, { bookType: 'xls', type: 'base64' });
    const a = document.createElement('a');
    const fn = `exact_werklijst_${new Date().toISOString().slice(0, 10)}.xls`;
    a.href = `data:application/vnd.ms-excel;base64,${b64}`; a.download = fn; a.click();
    toast.success(`Export: ${fn}`);
    try { await api.post('/api/storno/export', { filename: fn, metadata: { type: 'Exact Werklijst', total: werklijst.length } }); } catch(e) { console.error('[Storno] Export log failed:', e); }
  };

  const clear = () => { setFile(null); setFileNames([]); setResult(null); setError(''); if (fileRef.current) fileRef.current.value = ''; };

  const s = result?.summary;

  // ─── Tab 2: Reïncasso Selector logic ───
  const STAT = {
    klaar:      { label: 'Klaar voor reïncasso', color: '#059669', bg: '#05966910', icon: CheckCircle },
    wachten:    { label: 'Nog wachten', color: '#ca8a04', bg: '#ca8a0410', icon: Clock },
    handmatig2: { label: 'Handmatige controle', color: '#d97706', bg: '#d9770610', icon: HelpCircle },
    uitgesloten:{ label: 'Uitgesloten', color: '#dc2626', bg: '#dc262610', icon: XCircle },
  };

  const reincassoData = useMemo(() => {
    if (!result?.rows) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rows = result.rows.map((r, idx) => {
      const rLow = (r.reden + ' ' + r.omschrijving).toLowerCase();
      // Hard block?
      if (HARD_BLOCK.some(b => rLow.includes(b))) {
        return { ...r, _idx: idx, reStatus: 'uitgesloten', reReden: 'Harde blokkade: ' + r.reden, warnings: [] };
      }
      // Parse date
      let stornoDt = null;
      if (r.datum) {
        const p = r.datum.match(/(\d{2})-(\d{2})-(\d{4})/);
        if (p) stornoDt = new Date(+p[3], +p[2] - 1, +p[1]);
      }
      // Wachtdagen check
      let daysPassed = stornoDt ? Math.floor((today - stornoDt) / 86400000) : 999;
      const wachtOk = daysPassed >= wachtdagen;
      // Duplicates + high amount → computed later
      return { ...r, _idx: idx, stornoDt, daysPassed, wachtOk, reStatus: null, reReden: '', warnings: [] };
    });

    // Duplicaat detectie
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].reStatus === 'uitgesloten') continue;
      for (let j = 0; j < rows.length; j++) {
        if (i === j) continue;
        const a = rows[i], b = rows[j];
        const w = [];
        if (a.naam && b.naam && a.naam.toLowerCase() === b.naam.toLowerCase() && a.bedrag === b.bedrag) w.push('Zelfde klant + bedrag');
        if (a.iban && b.iban && a.iban === b.iban && a.bedrag === b.bedrag) w.push('Zelfde IBAN + bedrag');
        if (a.machtiging && b.machtiging && a.machtiging === b.machtiging) w.push('Zelfde Machtiging ID');
        if (a.referentie && b.referentie && a.referentie === b.referentie) w.push('Zelfde Kenmerk');
        if (w.length) rows[i].warnings = [...new Set([...rows[i].warnings, ...w])];
      }
    }

    // Determine status
    rows.forEach(r => {
      if (r.reStatus === 'uitgesloten') return;
      if (r.warnings.length > 0 || r.bedrag > hoogDrempel) {
        r.reStatus = 'handmatig2';
        const reasons = [...r.warnings];
        if (r.bedrag > hoogDrempel) reasons.push(`Bedrag > €${fmt(hoogDrempel)}`);
        r.reReden = reasons.join('; ');
      } else if (!r.wachtOk) {
        r.reStatus = 'wachten';
        r.reReden = `Nog ${wachtdagen - r.daysPassed} dag(en) wachten`;
      } else {
        r.reStatus = 'klaar';
        r.reReden = 'Klaar voor opnieuw incasseren';
      }
    });
    return rows;
  }, [result, wachtdagen, hoogDrempel]);

  const reSummary = useMemo(() => {
    const cnt = { klaar: 0, wachten: 0, handmatig2: 0, uitgesloten: 0 };
    const amt = { klaar: 0, wachten: 0, handmatig2: 0, uitgesloten: 0 };
    reincassoData.forEach(r => { cnt[r.reStatus]++; amt[r.reStatus] = Math.round((amt[r.reStatus] + r.bedrag) * 100) / 100; });
    return { cnt, amt, total: reincassoData.length };
  }, [reincassoData]);

  const exportReincasso = async () => {
    const wb = XLSX.utils.book_new();
    const hdr = ['Datum', 'Klantnaam', 'IBAN', 'Bedrag', 'Reden', 'Status', 'Toelichting', 'Waarschuwingen'];
    const mapRow = r => [r.datum, r.naam, r.iban, r.bedrag, r.reden, STAT[r.reStatus]?.label || r.reStatus, r.reReden, r.warnings.join('; ')];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...reincassoData.map(mapRow)]), 'Alles');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...reincassoData.filter(r => r.reStatus === 'klaar').map(mapRow)]), 'Klaar');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...reincassoData.filter(r => r.reStatus === 'wachten').map(mapRow)]), 'Wachten');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...reincassoData.filter(r => r.reStatus === 'handmatig2').map(mapRow)]), 'Handmatig');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...reincassoData.filter(r => r.reStatus === 'uitgesloten').map(mapRow)]), 'Uitgesloten');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Status', 'Aantal', 'Totaalbedrag'],
      ['Klaar voor reïncasso', reSummary.cnt.klaar, reSummary.amt.klaar],
      ['Nog wachten', reSummary.cnt.wachten, reSummary.amt.wachten],
      ['Handmatige controle', reSummary.cnt.handmatig2, reSummary.amt.handmatig2],
      ['Uitgesloten', reSummary.cnt.uitgesloten, reSummary.amt.uitgesloten],
      ['', '', ''], ['Totaal', reSummary.total, reincassoData.reduce((s, r) => s + r.bedrag, 0)],
    ]), 'Samenvatting');
    const b64 = XLSX.write(wb, { bookType: 'xls', type: 'base64' });
    const a = document.createElement('a');
    const fn = `reincasso_selectie_${new Date().toISOString().slice(0, 10)}.xls`;
    a.href = `data:application/vnd.ms-excel;base64,${b64}`; a.download = fn; a.click();
    toast.success(`Export: ${fn}`);
    try { await api.post('/api/storno/export', { filename: fn, metadata: { type: 'Reincasso Selectie', ...reSummary.cnt } }); } catch(e) { console.error('[Storno] Export log failed:', e); }
  };

  const exportReincassoCSV = () => {
    const klaar = reincassoData.filter(r => r.reStatus === 'klaar');
    const lines = ['Datum;Klantnaam;IBAN;Bedrag;Reden'];
    klaar.forEach(r => lines.push(`${r.datum};${r.naam};${r.iban};${r.bedrag};${r.reden}`));
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `werklijst_incasso_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`CSV: ${klaar.length} regels`);
  };

  return (
    <div className="tw1-fade-in" data-testid="storno-page">
      <HeroBanner title="Storno Incasso Manager" subtitle="Terugboekingen analyseren, classificeren en verwerken" variant="indigo" icon={RefreshCw} />

      <Tabs value={mainTab} onValueChange={setMainTab} className="space-y-5">
        <TabsList className="bg-gray-100 p-0.5" data-testid="storno-tabs">
          <TabsTrigger value="analyzer" className="text-xs px-4 py-1.5" data-testid="storno-tab-analyzer">Storno Analyzer</TabsTrigger>
          <TabsTrigger value="reincasso" className="text-xs px-4 py-1.5" disabled={!result} data-testid="storno-tab-reincasso">Reïncasso Selector</TabsTrigger>
          <TabsTrigger value="werklijst" className="text-xs px-4 py-1.5" data-testid="storno-tab-werklijst">Exact Werklijst</TabsTrigger>
        </TabsList>

        <TabsContent value="analyzer" className="space-y-5">
          {/* Upload */}
          {!file ? (
            <div className="tw1-dropzone p-8 text-center" onClick={() => fileRef.current?.click()} data-testid="storno-dropzone">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf" multiple className="hidden" onChange={e => handleUpload(e.target.files)} data-testid="storno-file-input" />
              <Upload className="w-10 h-10 mx-auto mb-3" style={{ color: C }} />
              <p className="text-sm font-medium text-[#0b1220]">Upload bankoverzicht</p>
              <p className="text-xs text-[#526071] mt-1">CSV, Excel of PDF — meerdere bestanden mogelijk</p>
            </div>
          ) : (
            <div className="tw1-card p-4 flex items-center justify-between" data-testid="storno-file-info">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5" style={{ color: C }} />
                <div>
                  <p className="text-sm font-medium text-[#0b1220]">{fileNames.length > 1 ? `${fileNames.length} bestanden` : fileNames[0]}</p>
                  {fileNames.length > 1 && <p className="text-xs text-[#526071]">{fileNames.join(', ')}</p>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={clear} data-testid="storno-clear-btn"><Trash2 className="w-4 h-4 text-gray-400" /></Button>
            </div>
          )}

          {loading && (
            <div className="tw1-card p-8 text-center">
              <div className="animate-spin w-8 h-8 border-3 rounded-full mx-auto" style={{ borderColor: `${C}30`, borderTopColor: C }} />
              <p className="text-sm text-[#526071] mt-3">Analyseren...</p>
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-[#c0392b]" data-testid="storno-error">{error}</div>}

          {/* ING banner */}
          {result?.is_ing && (
            <div className="rounded-xl p-3 border text-sm flex items-center gap-2" style={{ background: `${C}08`, borderColor: `${C}25` }} data-testid="storno-ing-banner">
              <Info className="w-4 h-4" style={{ color: C }} />
              <span style={{ color: C }} className="font-medium">ING-formaat herkend — alleen afschrijvingen (Af) worden getoond</span>
            </div>
          )}

          {/* Summary Cards */}
          {s && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="storno-summary">
              <div className="tw1-card p-4 text-center">
                <p className="text-2xl font-bold font-mono text-[#0b1220]">{s.total}</p>
                <p className="text-xs text-[#526071] mt-1">Totaal</p>
              </div>
              {Object.entries(CATS).map(([key, cat]) => (
                <div key={key} className="tw1-card p-4 text-center" style={{ borderLeft: `3px solid ${cat.color}` }}>
                  <p className="text-2xl font-bold font-mono" style={{ color: cat.color }}>{s[key] || 0}</p>
                  <p className="text-xs text-[#526071] mt-1">{cat.label}</p>
                  <p className="text-xs font-mono mt-0.5" style={{ color: cat.color }}>{'€'} {fmt(s[`bedrag_${key}`] || 0)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Export button */}
          {result && (
            <Button onClick={exportAnalyse} className="text-white text-sm" style={{ background: C }} data-testid="storno-export-btn">
              <Download className="w-4 h-4 mr-2" /> Exporteer Analyse (.xls — 5 tabbladen)
            </Button>
          )}

          {/* Results Table */}
          {result?.rows?.length > 0 && (
            <div className="tw1-card overflow-hidden" data-testid="storno-results-table">
              <div className="overflow-x-auto">
                <table className="w-full tw1-table">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2.5 text-left text-xs">Datum</th>
                      <th className="px-3 py-2.5 text-left text-xs">Klantnaam</th>
                      <th className="px-3 py-2.5 text-left text-xs">IBAN</th>
                      <th className="px-3 py-2.5 text-right text-xs">Bedrag</th>
                      <th className="px-3 py-2.5 text-left text-xs">Reden</th>
                      <th className="px-3 py-2.5 text-center text-xs">Classificatie</th>
                      <th className="px-3 py-2.5 text-left text-xs">Advies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r, i) => {
                      const cat = CATS[r.classificatie] || CATS.handmatig;
                      const CatIcon = cat.icon;
                      return (
                        <tr key={i} style={{ backgroundColor: cat.bg }} data-testid={`storno-row-${i}`}>
                          <td className="px-3 py-2 text-xs text-[#526071] whitespace-nowrap">{r.datum}</td>
                          <td className="px-3 py-2 text-sm font-medium text-[#0b1220]">{r.naam}</td>
                          <td className="px-3 py-2 text-xs font-mono text-[#526071]">{r.iban}</td>
                          <td className="px-3 py-2 text-sm font-mono text-right" style={{ color: cat.color }}>{'€'} {fmt(r.bedrag)}</td>
                          <td className="px-3 py-2 text-xs text-[#526071] max-w-[180px] truncate">{r.reden || r.omschrijving}</td>
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: cat.color, backgroundColor: cat.bg, border: `1px solid ${cat.color}25` }}>
                              <CatIcon className="w-3 h-3" />{cat.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-[#3a4553]">{r.advies}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="reincasso" className="space-y-5">
          {!result ? (
            <div className="tw1-card p-8 text-center text-sm text-[#526071]">Upload eerst een bestand in de Storno Analyzer tab</div>
          ) : (
            <>
              {/* Settings */}
              <div className="tw1-card p-4 flex flex-wrap items-center gap-6" data-testid="reincasso-settings">
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4" style={{ color: C }} />
                  <span className="text-sm text-[#3a4553]">Wachtdagen:</span>
                  <input type="number" min="0" max="30" value={wachtdagen} onChange={e => setWachtdagen(Math.max(0, +e.target.value || 0))}
                    className="w-16 h-8 border border-[#e0e4ea] rounded-lg px-2 text-sm text-center focus:outline-none focus:ring-2" style={{ '--tw-ring-color': `${C}40` }} data-testid="reincasso-wachtdagen" />
                </div>
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4" style={{ color: C }} />
                  <span className="text-sm text-[#3a4553]">Hoog bedrag drempel: {'€'}</span>
                  <input type="number" min="0" step="100" value={hoogDrempel} onChange={e => setHoogDrempel(Math.max(0, +e.target.value || 0))}
                    className="w-24 h-8 border border-[#e0e4ea] rounded-lg px-2 text-sm text-center focus:outline-none focus:ring-2" style={{ '--tw-ring-color': `${C}40` }} data-testid="reincasso-drempel" />
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="reincasso-summary">
                <div className="tw1-card p-4 text-center">
                  <p className="text-2xl font-bold font-mono text-[#0b1220]">{reSummary.total}</p>
                  <p className="text-xs text-[#526071] mt-1">Totaal</p>
                </div>
                {Object.entries(STAT).map(([key, st]) => (
                  <div key={key} className="tw1-card p-4 text-center" style={{ borderLeft: `3px solid ${st.color}` }}>
                    <p className="text-2xl font-bold font-mono" style={{ color: st.color }}>{reSummary.cnt[key]}</p>
                    <p className="text-xs text-[#526071] mt-1">{st.label}</p>
                    <p className="text-xs font-mono mt-0.5" style={{ color: st.color }}>{'€'} {fmt(reSummary.amt[key])}</p>
                  </div>
                ))}
              </div>

              {/* Export */}
              <div className="flex gap-3">
                <Button onClick={exportReincasso} className="text-white text-sm" style={{ background: C }} data-testid="reincasso-export-xls">
                  <Download className="w-4 h-4 mr-2" /> Excel (6 tabbladen)
                </Button>
                <Button onClick={exportReincassoCSV} variant="outline" className="text-sm" disabled={reSummary.cnt.klaar === 0} data-testid="reincasso-export-csv">
                  <Download className="w-4 h-4 mr-2" /> CSV werklijst ({reSummary.cnt.klaar} klaar)
                </Button>
              </div>

              {/* Table */}
              <div className="tw1-card overflow-hidden" data-testid="reincasso-table">
                <div className="overflow-x-auto">
                  <table className="w-full tw1-table">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2.5 text-left text-xs">Datum</th>
                        <th className="px-3 py-2.5 text-left text-xs">Klantnaam</th>
                        <th className="px-3 py-2.5 text-left text-xs">IBAN</th>
                        <th className="px-3 py-2.5 text-right text-xs">Bedrag</th>
                        <th className="px-3 py-2.5 text-left text-xs">Reden</th>
                        <th className="px-3 py-2.5 text-center text-xs">Status</th>
                        <th className="px-3 py-2.5 text-left text-xs">Toelichting</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reincassoData.map((r, i) => {
                        const st = STAT[r.reStatus] || STAT.handmatig2;
                        const StIcon = st.icon;
                        return (
                          <tr key={i} style={{ backgroundColor: st.bg }} data-testid={`reincasso-row-${i}`}>
                            <td className="px-3 py-2 text-xs text-[#526071] whitespace-nowrap">{r.datum}</td>
                            <td className="px-3 py-2 text-sm font-medium text-[#0b1220]">{r.naam}</td>
                            <td className="px-3 py-2 text-xs font-mono text-[#526071]">{r.iban}</td>
                            <td className="px-3 py-2 text-sm font-mono text-right" style={{ color: st.color }}>{'€'} {fmt(r.bedrag)}</td>
                            <td className="px-3 py-2 text-xs text-[#526071] max-w-[150px] truncate">{r.reden || r.omschrijving}</td>
                            <td className="px-3 py-2 text-center">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: st.color, backgroundColor: st.bg, border: `1px solid ${st.color}25` }}>
                                <StIcon className="w-3 h-3" />{st.label}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-[#3a4553]">
                              {r.reReden}
                              {r.warnings.length > 0 && (
                                <div className="flex items-center gap-1 mt-1 text-[#d97706]">
                                  <AlertTriangle className="w-3 h-3" /><span>{r.warnings.join(', ')}</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>
        <TabsContent value="werklijst" className="space-y-5">
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="werklijst-stats">
            {['nieuw', 'in behandeling', 'wachten op klant', 'afgerond'].map(s => (
              <div key={s} className="tw1-card p-4 text-center" style={{ borderLeft: `3px solid ${WERK_COLORS[s]}` }}>
                <p className="text-2xl font-bold font-mono" style={{ color: WERK_COLORS[s] }}>{wlStats.cnt[s] || 0}</p>
                <p className="text-xs text-[#526071] mt-1 capitalize">{s}</p>
                <p className="text-xs font-mono mt-0.5" style={{ color: WERK_COLORS[s] }}>{'€'} {fmt(wlStats.amt[s] || 0)}</p>
              </div>
            ))}
          </div>

          {/* Actions bar */}
          <div className="tw1-card p-3 flex flex-wrap items-center gap-3" data-testid="werklijst-actions">
            {result && <Button onClick={importToWerklijst} size="sm" className="text-xs text-white" style={{ background: C }} data-testid="werklijst-import-btn"><Import className="w-3 h-3 mr-1" /> Importeer uit analyse ({result.summary.total})</Button>}
            <Button onClick={exportWerklijst} size="sm" variant="outline" className="text-xs" disabled={werklijst.length === 0} data-testid="werklijst-export-btn"><Download className="w-3 h-3 mr-1" /> Excel (7 tabbladen)</Button>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-[#526071]" />
              <input placeholder="Zoek klant/IBAN..." value={wlFilter.search} onChange={e => setWlFilter(p => ({ ...p, search: e.target.value }))}
                className="w-40 h-7 border border-[#e0e4ea] rounded-lg px-2 text-xs focus:outline-none" data-testid="werklijst-search" />
            </div>
            <select value={wlFilter.werkstatus} onChange={e => setWlFilter(p => ({ ...p, werkstatus: e.target.value }))} className="h-7 border border-[#e0e4ea] rounded-lg px-2 text-xs" data-testid="werklijst-filter-ws">
              <option value="">Alle statussen</option>
              {WERK_ST.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={wlFilter.medewerker} onChange={e => setWlFilter(p => ({ ...p, medewerker: e.target.value }))} className="h-7 border border-[#e0e4ea] rounded-lg px-2 text-xs" data-testid="werklijst-filter-med">
              <option value="">Alle medewerkers</option>
              {MEDEW.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div className="flex gap-5">
            {/* Table */}
            <div className={`tw1-card overflow-hidden ${selectedItem ? 'flex-1' : 'w-full'}`} data-testid="werklijst-table">
              {wlLoading ? (
                <div className="p-8 text-center"><div className="animate-spin w-6 h-6 border-2 rounded-full mx-auto" style={{ borderColor: `${C}30`, borderTopColor: C }} /></div>
              ) : filteredWl.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#526071]">{werklijst.length === 0 ? 'Geen items. Importeer data vanuit Tab 1.' : 'Geen resultaten voor dit filter.'}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full tw1-table">
                    <thead><tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left text-xs">Klant</th>
                      <th className="px-3 py-2 text-right text-xs">Bedrag</th>
                      <th className="px-3 py-2 text-left text-xs">Werkstatus</th>
                      <th className="px-3 py-2 text-left text-xs">Medewerker</th>
                    </tr></thead>
                    <tbody>
                      {filteredWl.map(w => (
                        <tr key={w.id} onClick={() => selectWlItem(w)} className={`cursor-pointer hover:bg-gray-50 ${selectedItem?.id === w.id ? 'bg-indigo-50/50' : ''}`} data-testid={`werklijst-row-${w.id}`}>
                          <td className="px-3 py-2"><p className="text-sm font-medium text-[#0b1220] truncate max-w-[200px]">{w.naam}</p><p className="text-xs text-[#526071]">{w.datum}</p></td>
                          <td className="px-3 py-2 text-sm font-mono text-right">{'€'} {fmt(w.bedrag)}</td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: WERK_COLORS[w.werkstatus] || C, background: (WERK_COLORS[w.werkstatus] || C) + '12' }}>{w.werkstatus}</span></td>
                          <td className="px-3 py-2 text-xs text-[#526071]">{w.medewerker}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Side panel */}
            {selectedItem && (
              <div className="w-80 shrink-0 tw1-card p-4 space-y-3 sticky top-4 max-h-[calc(100vh-200px)] overflow-y-auto" data-testid="werklijst-panel">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-[#0b1220]">Dossier</h3>
                  <button onClick={closePanel}><X className="w-4 h-4 text-[#526071]" /></button>
                </div>
                <div className="text-sm font-medium text-[#0b1220]">{selectedItem.naam}</div>
                <div className="text-xs text-[#526071]">{selectedItem.iban} &bull; {'€'} {fmt(selectedItem.bedrag)} &bull; {selectedItem.datum}</div>
                {selectedItem.reden && <div className="text-xs text-[#526071]">Reden: {selectedItem.reden}</div>}

                <div className="space-y-2.5 pt-2 border-t border-gray-100">
                  <label className="block"><span className="text-xs text-[#526071]">Medewerker</span>
                    <select value={editData.medewerker || ''} onChange={e => setEditData(p => ({ ...p, medewerker: e.target.value }))} className="w-full h-8 border border-[#e0e4ea] rounded-lg px-2 text-xs mt-0.5" data-testid="panel-medewerker">
                      {MEDEW.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </label>
                  <label className="block"><span className="text-xs text-[#526071]">Werkstatus</span>
                    <select value={editData.werkstatus || ''} onChange={e => setEditData(p => ({ ...p, werkstatus: e.target.value }))} className="w-full h-8 border border-[#e0e4ea] rounded-lg px-2 text-xs mt-0.5" data-testid="panel-werkstatus">
                      {WERK_ST.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="block"><span className="text-xs text-[#526071]">Dossierstatus</span>
                    <select value={editData.dossierstatus || ''} onChange={e => setEditData(p => ({ ...p, dossierstatus: e.target.value }))} className="w-full h-8 border border-[#e0e4ea] rounded-lg px-2 text-xs mt-0.5" data-testid="panel-dossierstatus">
                      {DOSSIER_ST.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="block"><span className="text-xs text-[#526071]">Notitie</span>
                    <textarea value={editData.notitie || ''} onChange={e => setEditData(p => ({ ...p, notitie: e.target.value }))} rows={2} className="w-full border border-[#e0e4ea] rounded-lg px-2 py-1 text-xs mt-0.5 resize-none" data-testid="panel-notitie" />
                  </label>
                  <label className="block"><span className="text-xs text-[#526071]">Volgende actiedatum</span>
                    <input type="date" value={editData.volgende_actiedatum || ''} onChange={e => setEditData(p => ({ ...p, volgende_actiedatum: e.target.value }))} className="w-full h-8 border border-[#e0e4ea] rounded-lg px-2 text-xs mt-0.5" data-testid="panel-actiedatum" />
                  </label>
                  <label className="block"><span className="text-xs text-[#526071]">Datum heropend</span>
                    <input type="date" value={editData.datum_heropend || ''} onChange={e => setEditData(p => ({ ...p, datum_heropend: e.target.value }))} className="w-full h-8 border border-[#e0e4ea] rounded-lg px-2 text-xs mt-0.5" />
                  </label>
                  <label className="block"><span className="text-xs text-[#526071]">Datum opnieuw geselecteerd</span>
                    <input type="date" value={editData.datum_opnieuw_geselecteerd || ''} onChange={e => setEditData(p => ({ ...p, datum_opnieuw_geselecteerd: e.target.value }))} className="w-full h-8 border border-[#e0e4ea] rounded-lg px-2 text-xs mt-0.5" />
                  </label>
                  <div className="space-y-1.5 pt-1">
                    {[['exact_verwerkt', 'Exact verwerkt'], ['contact_klant_nodig', 'Contact klant nodig'], ['klantcontact_gedaan', 'Klantcontact gedaan']].map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!editData[k]} onChange={e => setEditData(p => ({ ...p, [k]: e.target.checked }))} className="rounded border-gray-300" />
                        <span className="text-xs text-[#3a4553]">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <Button onClick={saveItem} size="sm" className="flex-1 text-xs text-white" style={{ background: C }} data-testid="panel-save"><Save className="w-3 h-3 mr-1" /> Opslaan</Button>
                  <Button onClick={markAfgerond} size="sm" variant="outline" className="flex-1 text-xs text-[#059669]" data-testid="panel-afgerond"><CheckCircle className="w-3 h-3 mr-1" /> Afgerond</Button>
                </div>
              </div>
            )}
          </div>

          {/* Debiteur Samenvatting */}
          {debSummary.length > 0 && (
            <div className="tw1-card overflow-hidden" data-testid="werklijst-debiteur">
              <div className="p-3 border-b border-gray-100"><h3 className="text-sm font-bold text-[#0b1220]">Per debiteur</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full tw1-table"><thead><tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs">Klantnaam</th><th className="px-3 py-2 text-right text-xs">Dossiers</th><th className="px-3 py-2 text-right text-xs">Totaal</th><th className="px-3 py-2 text-right text-xs">Open</th><th className="px-3 py-2 text-right text-xs">Afgerond</th>
                </tr></thead><tbody>
                  {debSummary.map(d => (
                    <tr key={d.naam}><td className="px-3 py-2 text-sm font-medium">{d.naam}</td><td className="px-3 py-2 text-sm font-mono text-right">{d.count}</td><td className="px-3 py-2 text-sm font-mono text-right">{'€'} {fmt(d.totaal)}</td><td className="px-3 py-2 text-sm font-mono text-right text-[#d97706]">{d.open}</td><td className="px-3 py-2 text-sm font-mono text-right text-[#059669]">{d.afgerond}</td></tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
